import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { companyService, paymentService } from '../../services'
import PageLayout from '../../components/layout/page-layout'
import { Container } from '../../components/ui/container'
import { PageHeader } from '../../components/layout/page-header'
import { PagePanel, PagePanelHeader } from '../../components/layout/page-panel'
import { Badge } from '../../components/ui/badge'
import type { PaymentPackage } from '../../services'
import { useLocale } from '../../hooks/use-locale'
import { subscriptionStatusToText } from '../../helpers/enum-to-text'

function getStatusVariant(status?: string): 'default' | 'success' | 'warning' | 'error' {
  const normalized = (status || '').toLowerCase()
  if (normalized === 'active' || normalized === 'trialing') return 'success'
  if (normalized === 'past_due' || normalized === 'unpaid') return 'warning'
  if (normalized === 'canceled' || normalized === 'incomplete_expired') return 'error'
  return 'default'
}

function parseEuroAmount(value: string | number) {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0
  }

  const normalized = value.trim().replace(/\s/g, '').replace(',', '.')
  const parsed = Number(normalized)

  return Number.isFinite(parsed) ? parsed : 0
}

function formatEuroAmountInput(value?: number) {
  if (value === undefined || value === null || Number.isNaN(value)) return ''
  return value.toFixed(2).replace('.', ',')
}

export default function SuperadminBillingOverviewPage() {
  const { t } = useTranslation()
  const { formatDate, formatNumber } = useLocale()
  const queryClient = useQueryClient()
  const [editablePackages, setEditablePackages] = useState<PaymentPackage[]>([])
  const [amountInputs, setAmountInputs] = useState<Record<string, string>>({})

  const formatDisplayDate = (value?: string) => {
    if (!value) return t('common.dash')
    return formatDate(value, { day: '2-digit', month: '2-digit', year: 'numeric' })
  }

  const {
    data: companiesData,
    isLoading: companiesLoading,
    error: companiesError,
  } = useQuery({
    queryKey: ['companies', 'billing-overview'],
    queryFn: () => companyService.getCompanies({ page: 1, limit: 100 }),
  })

  const {
    data: packagesData,
    isLoading: packagesLoading,
    error: packagesError,
  } = useQuery({
    queryKey: ['payment-packages', 'manage'],
    queryFn: () => paymentService.listPackagesForManagement(),
  })

  const packages = useMemo(() => packagesData ?? [], [packagesData])

  const {
    data: invoices = [],
    isLoading: invoicesLoading,
    error: invoicesError,
  } = useQuery({
    queryKey: ['invoices', 'superadmin'],
    queryFn: () => paymentService.listInvoices(),
  })

  useEffect(() => {
    if (!packagesData) return

    setEditablePackages(packagesData)
    setAmountInputs(
      Object.fromEntries(packagesData.map(pkg => [pkg.id, formatEuroAmountInput(pkg.amount)]))
    )
  }, [packagesData])

  const updatePackageMutation = useMutation({
    mutationFn: (pkg: PaymentPackage) =>
      paymentService.updatePackage(pkg.id, {
        name: pkg.name,
        description: pkg.description,
        amount: Number(pkg.amount),
        currency: pkg.currency,
        billingInterval: pkg.billingInterval,
        enabledModules: pkg.enabledModules,
        maxLicenses: pkg.maxLicenses,
        isActive: pkg.isActive,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-packages', 'manage'] })
      queryClient.invalidateQueries({ queryKey: ['payment-packages', 'active'] })
    },
  })

  const companies = useMemo(() => companiesData?.companies ?? [], [companiesData?.companies])

  const subscriptionQueryConfigs = useMemo(
    () =>
      companies.map(company => ({
        queryKey: ['company-subscription', company.id],
        queryFn: () => paymentService.getSubscription(company.id),
        enabled: Boolean(company.id),
      })),
    [companies]
  )

  const subscriptionQueries = useQueries({
    queries: subscriptionQueryConfigs,
  })

  const subscriptionByCompanyId = useMemo(() => {
    const entries = companies.map(
      (company, index) => [company.id, subscriptionQueries[index]?.data || null] as const
    )
    return new Map(entries)
  }, [companies, subscriptionQueries])

  const packageNameById = useMemo(() => {
    return new Map(packages.map(pkg => [pkg.id, pkg.name]))
  }, [packages])

  const isLoadingSubscriptions = subscriptionQueries.some(query => query.isLoading)
  const subscriptionsError = subscriptionQueries.find(query => query.error)?.error

  const updateEditablePackage = (id: string, updater: (pkg: PaymentPackage) => PaymentPackage) => {
    setEditablePackages(prev => prev.map(pkg => (pkg.id === id ? updater(pkg) : pkg)))
  }

  return (
    <PageLayout
      isLoading={companiesLoading || packagesLoading || isLoadingSubscriptions}
      error={companiesError || packagesError || subscriptionsError}
      loadingMessage={t('billing.loading')}
      errorMessage={t('billing.loadError')}
    >
      <Container>
        <div className='space-y-6'>
          <PageHeader title={t('billing.title')} subtitle={t('billing.subtitle')} />

          <PagePanel padding={false}>
            <PagePanelHeader
              title={t('billing.managePackagesTitle')}
              description={
                <>
                  <p>{t('billing.managePackagesHint1')}</p>
                  <p className='mt-1'>
                    {t('billing.managePackagesHint2')}{' '}
                    <Link
                      to='/subscription'
                      className='text-primary-700 hover:text-primary-800 underline'
                    >
                      {t('billing.subscriptionAndLimitsLink')}
                    </Link>
                    .
                  </p>
                </>
              }
            />

            <table className='w-full'>
              <thead className='bg-surface-secondary border-b border-primary-100'>
                <tr>
                  <th className='px-3 py-2 text-left text-xs font-semibold text-neutral-700'>
                    {t('common.name')}
                  </th>
                  <th className='px-3 py-2 text-left text-xs font-semibold text-neutral-700'>
                    {t('common.price')}
                  </th>
                  <th className='px-3 py-2 text-left text-xs font-semibold text-neutral-700'>
                    {t('common.licenses')}
                  </th>
                  <th className='px-3 py-2 text-left text-xs font-semibold text-neutral-700'>
                    {t('common.employees')}
                  </th>
                  <th className='px-3 py-2 text-left text-xs font-semibold text-neutral-700'>
                    {t('common.active')}
                  </th>
                  <th className='px-3 py-2 text-right text-xs font-semibold text-neutral-700'>
                    {t('billing.save')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {editablePackages.map(pkg => {
                  const isSaving =
                    updatePackageMutation.isPending &&
                    updatePackageMutation.variables?.id === pkg.id

                  return (
                    <tr
                      key={pkg.id}
                      className='border-b border-primary-100 last:border-b-0 hover:bg-surface-card-hover transition-colors'
                    >
                      <td className='px-3 py-2'>
                        <input
                          value={pkg.name}
                          onChange={e =>
                            updateEditablePackage(pkg.id, prev => ({
                              ...prev,
                              name: e.target.value,
                            }))
                          }
                          className='input-field w-full text-sm'
                        />
                      </td>
                      <td className='px-3 py-2'>
                        <input
                          type='text'
                          inputMode='decimal'
                          value={amountInputs[pkg.id] ?? formatEuroAmountInput(pkg.amount)}
                          onChange={e => {
                            const value = e.target.value.replace('.', ',')

                            if (!/^\d*(,\d{0,2})?$/.test(value)) return

                            setAmountInputs(prev => ({
                              ...prev,
                              [pkg.id]: value,
                            }))
                            updateEditablePackage(pkg.id, prev => ({
                              ...prev,
                              amount: parseEuroAmount(value),
                            }))
                          }}
                          onBlur={e => {
                            const formattedValue = formatEuroAmountInput(
                              parseEuroAmount(e.target.value)
                            )

                            setAmountInputs(prev => ({
                              ...prev,
                              [pkg.id]: formattedValue,
                            }))
                            updateEditablePackage(pkg.id, prev => ({
                              ...prev,
                              amount: parseEuroAmount(formattedValue),
                            }))
                          }}
                          placeholder={t('billing.pricePlaceholder')}
                          className='input-field w-28 text-sm'
                        />
                      </td>
                      <td className='px-3 py-2'>
                        <input
                          type='number'
                          min={1}
                          value={pkg.maxLicenses ?? ''}
                          onChange={e =>
                            updateEditablePackage(pkg.id, prev => ({
                              ...prev,
                              maxLicenses: Number(e.target.value || 1),
                            }))
                          }
                          className='input-field w-20 text-sm'
                        />
                      </td>
                      <td className='px-3 py-2'>
                        <input
                          type='number'
                          min={1}
                          value={pkg.maxLicenses ?? ''}
                          onChange={e =>
                            updateEditablePackage(pkg.id, prev => ({
                              ...prev,
                              maxLicenses: Number(e.target.value || 1),
                            }))
                          }
                          className='input-field w-24 text-sm'
                        />
                      </td>
                      <td className='px-3 py-2'>
                        <input
                          type='checkbox'
                          className='h-4 w-4 rounded border-primary-200 text-primary-600 focus:ring-primary-400'
                          checked={pkg.isActive}
                          onChange={e =>
                            updateEditablePackage(pkg.id, prev => ({
                              ...prev,
                              isActive: e.target.checked,
                            }))
                          }
                        />
                      </td>
                      <td className='px-3 py-2 text-right'>
                        <button
                          onClick={() =>
                            updatePackageMutation.mutate({
                              ...pkg,
                              amount: parseEuroAmount(amountInputs[pkg.id] ?? pkg.amount),
                            })
                          }
                          disabled={isSaving}
                          className='btn-primary-modern inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed'
                        >
                          {isSaving ? t('billing.saving') : t('billing.save')}
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </PagePanel>

          <PagePanel padding={false}>
            <table className='w-full'>
              <thead className='bg-surface-secondary border-b border-primary-100'>
                <tr>
                  <th className='px-4 py-3 text-left text-sm font-semibold text-neutral-900'>
                    {t('billing.company')}
                  </th>
                  <th className='px-4 py-3 text-left text-sm font-semibold text-neutral-900'>
                    {t('billing.package')}
                  </th>
                  <th className='px-4 py-3 text-left text-sm font-semibold text-neutral-900'>
                    {t('billing.status')}
                  </th>
                  <th className='px-4 py-3 text-left text-sm font-semibold text-neutral-900'>
                    {t('billing.orderedSince')}
                  </th>
                  <th className='px-4 py-3 text-left text-sm font-semibold text-neutral-900'>
                    {t('billing.periodEnd')}
                  </th>
                  <th className='px-4 py-3 text-left text-sm font-semibold text-neutral-900'>
                    {t('billing.cancellation')}
                  </th>
                  <th className='px-4 py-3 text-right text-sm font-semibold text-neutral-900'>
                    {t('common.actions')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {companies.map(company => {
                  const subscription = subscriptionByCompanyId.get(company.id)
                  const packageName = subscription?.packageId
                    ? packageNameById.get(subscription.packageId) || subscription.packageId
                    : t('common.dash')

                  return (
                    <tr
                      key={company.id}
                      className='border-b border-primary-100 last:border-b-0 hover:bg-surface-card-hover transition-colors'
                    >
                      <td className='px-4 py-3'>
                        <div className='font-medium text-neutral-900'>{company.name}</div>
                        <div className='text-xs text-neutral-500'>
                          {company.contactPersonEmail || t('common.dash')}
                        </div>
                      </td>
                      <td className='px-4 py-3 text-sm text-neutral-700'>{packageName}</td>
                      <td className='px-4 py-3'>
                        {subscription ? (
                          <Badge
                            title={subscriptionStatusToText(subscription.status)}
                            variant={getStatusVariant(subscription.status)}
                          />
                        ) : (
                          <Badge title={t('billing.noSubscription')} variant='default' />
                        )}
                      </td>
                      <td className='px-4 py-3 text-sm text-neutral-700'>
                        {formatDisplayDate(subscription?.currentPeriodStart)}
                      </td>
                      <td className='px-4 py-3 text-sm text-neutral-700'>
                        {formatDisplayDate(subscription?.currentPeriodEnd)}
                      </td>
                      <td className='px-4 py-3 text-sm text-neutral-700'>
                        {subscription?.cancelAtPeriodEnd
                          ? t('billing.cancelAtPeriodEnd')
                          : t('common.dash')}
                      </td>
                      <td className='px-4 py-3 text-right'>
                        <Link
                          to={`/companies/${company.id}/edit`}
                          className='btn-secondary-modern inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-medium'
                        >
                          {t('billing.company')}
                        </Link>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </PagePanel>

          <PagePanel padding={false}>
            <PagePanelHeader title={t('billing.invoicesTitle')} />

            {invoicesLoading ? (
              <div className='px-4 py-6 text-sm text-neutral-500'>{t('loading.invoices')}</div>
            ) : invoicesError ? (
              <div className='px-4 py-6 text-sm text-error-700'>{t('billing.loadInvoicesError')}</div>
            ) : invoices.length === 0 ? (
              <div className='px-4 py-6 text-sm text-neutral-500'>{t('billing.noInvoices')}</div>
            ) : (
              <table className='w-full'>
                <thead className='bg-surface-secondary border-b border-primary-100'>
                  <tr>
                    <th className='px-4 py-3 text-left text-xs font-semibold text-neutral-700'>
                      {t('billing.company')}
                    </th>
                    <th className='px-4 py-3 text-left text-xs font-semibold text-neutral-700'>
                      {t('common.date')}
                    </th>
                    <th className='px-4 py-3 text-left text-xs font-semibold text-neutral-700'>
                      {t('common.amount')}
                    </th>
                    <th className='px-4 py-3 text-left text-xs font-semibold text-neutral-700'>
                      {t('billing.status')}
                    </th>
                    <th className='px-4 py-3 text-right text-xs font-semibold text-neutral-700'>
                      {t('common.download')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map(invoice => (
                    <tr
                      key={`${invoice.companyId || 'na'}-${invoice.stripeInvoiceId}`}
                      className='border-b border-primary-100 last:border-b-0 hover:bg-surface-card-hover transition-colors'
                    >
                      <td className='px-4 py-3 text-sm text-neutral-700'>
                        {invoice.companyName || t('common.dash')}
                      </td>
                      <td className='px-4 py-3 text-sm text-neutral-700'>
                        {invoice.createdAt ? formatDisplayDate(invoice.createdAt) : t('common.dash')}
                      </td>
                      <td className='px-4 py-3 text-sm text-neutral-700'>
                        {formatNumber(invoice.amount, {
                          style: 'currency',
                          currency: (invoice.currency || 'eur').toUpperCase(),
                        })}
                      </td>
                      <td className='px-4 py-3'>
                        <Badge
                          title={subscriptionStatusToText(invoice.status)}
                          variant={getStatusVariant(invoice.status)}
                        />
                      </td>
                      <td className='px-4 py-3 text-right'>
                        {invoice.invoicePdfUrl || invoice.hostedInvoiceUrl ? (
                          <a
                            href={invoice.invoicePdfUrl || invoice.hostedInvoiceUrl}
                            target='_blank'
                            rel='noreferrer'
                            className='btn-primary-modern inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-medium !text-white'
                          >
                            {t('common.download')}
                          </a>
                        ) : (
                          <span className='text-xs text-neutral-400'>{t('common.dash')}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </PagePanel>
        </div>
      </Container>
    </PageLayout>
  )
}
